import { BaseSettings, BaseThemeType } from 'Shared/frontend/themes/BaseSettings';
import { fluidType } from 'Shared/common/FluidType';
import { merge } from 'Shared/frontend/merge';
import { DeepPartial } from 'Shared/frontend/SettingsProvider';
import { Oswald } from 'Shared/frontend/fonts/Oswald';
import { Alatsi } from 'Shared/frontend/fonts/Alatsi';
import { OpenSans } from 'Shared/frontend/fonts/OpenSans';
import { INav } from 'Common/config/INav';
import { Calendar } from 'Shared/frontend/icons/Calendar';
import { FoodMenu } from 'Shared/frontend/icons/FoodMenu';
import { Home } from 'Shared/frontend/icons/Home';
import { Contact } from 'Shared/frontend/icons/Contact';
import { lazy } from 'solid-js';



export class DifferentDrummerSettings extends BaseSettings
{
	palette()
	{
		return {
			background: 'black',
			text: 'white',
			feature: '#be1e2d', /* a red */
			feature2: '#f2eb67', /* yellow */
			dimBackground: '#2b2929' /* a gray */
		};
	}

	fontIncludes()
	{
		return [
			Oswald,
			Alatsi,
			OpenSans
		];
	}

	fonts() {
		return {
			text: '"Open Sans", sans-serif',
			heading: 'Oswald, sans-serif',
			buttons: '"Open Sans", sans-serif',
			desktopNav: 'Oswald, sans-serif',
			mobileNav: '"Open Sans", sans-serif',
			subNav: '"Open Sans", sans-serif',
			menuItem: '"Alatsi", sans-serif'
		};
	}

	text()
	{
		return merge(super.text(), {
			pageTitle: {
				fontWeight: 500,
				lineHeight: 1.3,
				textTransform: 'uppercase',
				textAlign: 'center',
				margin: '32px 0px 24px'
			},

			title: {
				fontWeight: 500,
				lineHeight: 1.3,
				textTransform: 'uppercase',
				margin: '16px 0'
			},

			teaser: {
				fontWeight: 700,
				letterSpacing: 2.2,
				margin: '1em 0px 2em',
				textTransform: 'uppercase',
			},

			tagline: {
				textTransform: 'uppercase',
				fontWeight: 700,
				letterSpacing: 2.2,
				margin: '1em 0px'
			},
		});
	}

	poster()
	{
		return merge(super.poster(), {
			shared: {
				root: {
					color: this.palette().text,
				},
				dateBar: {
					backgroundColor: this.palette().feature,
				}
			}
		});
	}

	nav()
	{
		const sizes = this.sizes();

		return merge(super.nav(),{
			desktop: {
				button: {
					fontSize: fluidType('px',sizes.endFontShrink, sizes.minDesktopWidth,16,25),
					':hover': {
						color: this.palette().text
					}
				},
				active: {
					color: this.palette().text
				},
			},
			mobile: {
				button: {
					backgroundColor: this.palette().background,

					':hover': {
						color: this.palette().text
					}
				},
				active: {
					color: this.palette().text
				},
				more: {
					button: {
						':hover': {
//TODO think about these defaults...							
							color: this.palette().text
						}
					}
				}
			}
		});
	}

	address() {
		return merge(super.address(),{
			color:  this.palette().feature2,

			phone: {
				color:  this.palette().feature2
			},
		});
	}

	page()
	{
		return merge(super.page(),{
			main: {
				marginTop: 8
			},
			widgets: {
				subNav: {
					button: {
						backgroundColor: this.palette().text
					}
				}
			},
			button: {
				border: 'solid 1px',
				backgroundColor: this.palette().background,

				':hover': {
					backgroundColor: this.palette().text,
					color: this.palette().background
				}
			},
			embeddedHtml: {
				a: {
					color: this.palette().feature2
				}
			}
		});
	}

	footer()
	{
		return merge(super.footer(), {
			divider: {
				borderColor: 'inherit'
			},

			title: {
				color: this.palette().text
			},
		});
	}

	siteTheme()
	{
		const base = super.siteTheme();
		const palette = this.palette();
		const fonts = this.fonts();
		const sizes = this.sizes();

		base.specials = {
			food: 'Food',
			drinks: 'Drinks'
		};

		return merge(base,<DeepPartial<BaseThemeType>>{
			home: {
				main: {
					marginTop: 32
				}
			},

			event: {
				subNav: {
					button: {
						backgroundColor: palette.text,
						color: palette.background,
						fontSize: '0.8em',
						padding: '0.5em'
					}
				},
			},

			a: {
				color: palette.feature2
			},

			menu: {
				section: {
					marginBottom: 48,
					paddingLeft: fluidType('px',sizes.flipToMobileNav,sizes.startMarginShrink,22,48),
					paddingRight: fluidType('px',sizes.flipToMobileNav,sizes.startMarginShrink,22,48),
				},
				item: {
					type: 'imageless',
					title: { 
						fontFamily: fonts.menuItem,
						fontSize: 20,
						fontWeight: 'bold'
					},
				},
			},
		});
	}

	navContent(): INav {
		return {
			desktop: [
				{label:'Home',		link:'frontend/Home'},
				{label:'Food',		link:'frontend/Food'},
				{label:'Drinks',	link:'frontend/Drinks'},
				{label:'Events',	link:'frontend/Events'},
				{label:'Functions',	link:'frontend/Hire'},
				{label:'Contact',	link:'frontend/Contact'}
			],
			mobile: {
				maxButtons: 5,
				includeMoreButton: true,
				links: [
					{label:'Home',		link:'frontend/Home',		icon:Home},
					{label:'Food',		link:'frontend/Food',		icon:FoodMenu},
					{label:'Drinks',	link:'frontend/Drinks',		icon:FoodMenu},
					{label:'Events',	link:'frontend/Events',		icon:Calendar},
					{label:'Contact',	link:'frontend/Contact',	icon:Contact},
				]
			}
		};
	}

	links()
	{
		return {
			...super.links(),

			'backend/Menus': {type:'page', page:'backend/Menu', target:'/admin/menu'},
		};
	}

	routes()
	{
		return class extends super.routes() {
			frontendContent()
			{
				return [
					...super.frontendContent(),
					{
						path: '/food',
						component: lazy(() => import('Shared/frontend/menuPage/MenuPage')),
					},
					{
						path: '/drinks',
						component: lazy(() => import('Shared/frontend/menuPage/MenuPage')),
					},
				];
			}
		};
	}
};

